<template>
  <div class="i-mt-0 create-form">
    <settings-section-title v-if="isEditFlow" id="create" class="i-mb-4"
      >Edit Webhook</settings-section-title
    >
    <settings-section-title v-else id="create" class="i-mb-4"
      >Create Webhook</settings-section-title
    >
    <breadcrumbs class="i-mb-12" :items="breadcrumbs"></breadcrumbs>

    <info-status
      :text="infoMessage.text"
      :status="infoMessage.status"
    ></info-status>

    <label
      for="webhook-url"
      class="i-text-subheading-2 i-text-neutral-120 i-mb-2"
      >Webhook URL</label
    >
    <b-form-input
      id="webhook-url"
      v-model="$v.tempWebhook.url.$model"
      data-testid="webhook-url"
      placeholder="https://www.example.com/webhook"
      class="i-form-input"
      :class="{
        error: hasAnyError($v.tempWebhook.url) && $v.tempWebhook.url.minLength,
      }"
    ></b-form-input>
    <div class="i-mt-4 i-text-body-3">
      <p
        v-if="
          hasAnyError($v.tempWebhook.url) &&
          !$v.tempWebhook.url.validUrl &&
          $v.tempWebhook.url.minLength
        "
        class="i-text-error-110"
        data-testid="webhook-url-error"
      >
        The URL you provided is invalid. Webhooks must be https.
      </p>
      <p v-if="isEditFlow && !testWebhookEnabled">
        Save webhook changes to allow testing.
      </p>
    </div>

    <label
      for="webhook-type"
      class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
      >Event Type</label
    >
    <div class="d-flex flex-row align-items-center">
      <i-select
        data-testid="webhook-type"
        :options="displayOptions"
        :default-value="tempWebhook.type"
        placeholder="Please select an event"
        :class="{ error: hasAnyError($v.tempWebhook.type) }"
        @option-selected="
          (option) => {
            tempWebhook.type = option;
          }
        "
      ></i-select>

      <test-webhook
        v-if="isEditFlow"
        :webhook="tempWebhook"
        :disabled="!testWebhookEnabled"
        @flow-start="infoMessage.text = ''"
        @on-status="setStatus"
      ></test-webhook>
    </div>

    <label
      v-if="isEditFlow"
      for="webhook-key"
      class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
      >Secret Key</label
    >
    <i-copy
      v-if="isEditFlow"
      data-testid="webhook-secret"
      type="password"
      element-id="full_token"
      :value="tempWebhook.secret"
    ></i-copy>

    <label
      for="webhook-description"
      class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
      >Description (optional)</label
    >
    <b-form-textarea
      v-model="$v.tempWebhook.description.$model"
      data-testid="webhook-description"
      class="i-form-textarea"
      :class="{
        error:
          hasAnyError($v.tempWebhook.description) &&
          !$v.tempWebhook.description.maxLength,
      }"
    ></b-form-textarea>
    <div class="i-mt-4 i-text-body-3 i-text-error-110">
      <p
        v-if="
          hasAnyError($v.tempWebhook.description) &&
          !$v.tempWebhook.description.maxLength
        "
      >
        Max length of 150 characters reached.
      </p>
    </div>

    <label
      v-if="isEditFlow"
      class="i-mt-10 i-text-subheading-2 i-text-neutral-120 i-mb-2"
      >Status</label
    >
    <i-toggle
      v-if="isEditFlow"
      :toggle-value="tempWebhook.enabled"
      @value-update="(v) => (tempWebhook.enabled = v)"
    ></i-toggle>

    <div class="d-flex justify-flex-start align-items-center">
      <i-button
        v-if="!isEditFlow"
        data-testid="webhook-create"
        text="Create"
        class="i-mt-14"
        size="lg"
        :loading="loading"
        :disabled="$v.$error || $v.$invalid"
        @button-click="createKey()"
      ></i-button>

      <i-button
        v-if="isEditFlow"
        data-testid="webhook-save"
        text="Save"
        class="i-mt-14"
        size="lg"
        :loading="loading"
        :disabled="$v.$error || $v.$invalid"
        @button-click="editKey()"
      ></i-button>

      <i-button
        :text="isEditFlow ? 'Back' : 'Cancel'"
        data-testid="webhook-cancel"
        class="i-mt-14 i-ml-6"
        type="neutral"
        subtype="subtle"
        size="lg"
        @button-click="goToList()"
      ></i-button>
    </div>
  </div>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import { mapState } from 'vuex';

import { iButton, iCopyInput, iSelect, iToggle } from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import InfoStatus from '@/V2/settings/shared/InfoStatus';
import {
  deprecatedWebhookTypes,
  webhookTypes,
} from '@/V2/settings/webhooks/WebhookTypes';
import TestWebhook from '@/V2/settings/webhooks/TestWebhook.vue';

export default {
  components: {
    Breadcrumbs,
    'test-webhook': TestWebhook,
    'info-status': InfoStatus,
    'i-button': iButton,
    'i-copy': iCopyInput,
    'i-select': iSelect,
    'i-toggle': iToggle,
    'settings-section-title': SettingsSectionTitle,
  },
  props: {
    webhookId: {
      type: String,
      default: undefined,
    },
    newlyCreated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'Webhooks',
          to: { name: 'webhooks-settings-list' },
        },
        {
          text: this.webhookId ? 'Edit Webhook' : 'Create Webhook',
          active: true,
        },
      ],
      infoMessage: {
        text: '',
        status: '',
      },
      infoMessageTimeout: undefined,
      isEditFlow: !!this.webhookId,
      tempWebhook: {
        id: this.webhookId || '',
        url: '',
        type: '',
        secret: '',
        description: '',
        enabled: true,
      },
      routeWebhook: undefined,
      displayOptions: [],
      options: [...webhookTypes],
      loading: false,
    };
  },
  validations: {
    tempWebhook: {
      url: {
        required,
        minLength: (value) => value.length > 4,
        validUrl: (value) => value.startsWith('https'),
      },
      type: {
        required,
      },
      description: {
        maxLength: maxLength(150),
      },
    },
  },
  computed: {
    ...mapState(['webhooks']),
    testWebhookEnabled() {
      return (
        this.tempWebhook.type === this.routeWebhook?.type &&
        this.tempWebhook.url === this.routeWebhook?.url
      );
    },
  },
  mounted() {
    if (this.newlyCreated) {
      this.setStatus('Webhook Created', 'success');
    }
    this.setupEditingWebhook();
    this.fetchWebhooks();
  },
  methods: {
    fetchWebhooks() {
      this.$store
        .dispatch('getWebhooks')
        .then(() => this.setupEditingWebhook());
    },
    hasAnyError(validation) {
      return validation.$dirty && validation.$error;
    },
    setupEditingWebhook() {
      this.routeWebhook = this.$store.state.webhooks.find(
        ({ id }) => id === this.tempWebhook.id,
      );

      this.tempWebhook.url = this.routeWebhook?.url || '';
      this.tempWebhook.type = this.routeWebhook?.type || '';
      this.tempWebhook.secret = this.routeWebhook?.secret || '';
      this.tempWebhook.description = this.routeWebhook?.description || '';
      this.tempWebhook.enabled =
        typeof this.routeWebhook?.enabled === 'boolean'
          ? this.routeWebhook.enabled
          : this.tempWebhook.enabled;
      const editingWebhookIsDeprecated = deprecatedWebhookTypes.find(
        (o) => o.value === this.tempWebhook.type,
      );
      this.displayOptions = editingWebhookIsDeprecated
        ? this.options.concat(deprecatedWebhookTypes)
        : this.options;
    },
    setLoading(value) {
      this.loading = value;
    },
    async createKey() {
      this.setLoading(true);
      try {
        const response = await this.$store.dispatch('addWebhook', {
          url: this.tempWebhook.url,
          type: this.tempWebhook.type,
          description: this.tempWebhook.description,
          enabled: this.tempWebhook.enabled,
        });
        this.$store.commit('addWebhook', response.data);
        this.setLoading(false);
        this.$router.push({
          name: 'webhooks-settings-edit',
          params: { webhookId: response.data.id, newlyCreated: true },
        });
      } catch (error) {
        this.setStatus(error.response.data.message);
      }
    },
    async editKey() {
      this.setLoading(true);
      try {
        const resp = await this.$store.dispatch('updateWebhook', {
          id: this.tempWebhook.id,
          url: this.tempWebhook.url,
          type: this.tempWebhook.type,
          description: this.tempWebhook.description,
          enabled: this.tempWebhook.enabled,
        });
        this.$store.commit('updateGeneric', {
          objectsName: 'webhooks',
          updatedObject: {
            ...this.routeWebhook,
            ...resp.data,
          },
        });
        this.setupEditingWebhook();
        this.setStatus('Webhook Saved', 'success');
        this.setLoading(false);
      } catch (error) {
        this.setStatus(error.response.data.message);
      }
    },
    setStatus(message, type = 'error') {
      this.setLoading(false);
      clearTimeout(this.infoMessageTimeout);
      this.infoMessage.status = type;
      this.infoMessage.text = message;
      this.infoMessageTimeout = setTimeout(() => {
        this.infoMessage.text = '';
      }, 8000);
    },
    goToList() {
      this.$router.push({ name: 'webhooks-settings-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.create-form {
  max-width: 500px;
}
</style>
